/* eslint-disable no-empty-pattern */
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLazyQuery, useQuery } from "@apollo/client";

import { toast } from "react-toastify";
import { useAppSelector } from "../../../redux/hooks";
import { AdminUserModel } from "../../../redux/types";
import { CreateUser } from "./CreateUser";
import { UserTable } from "./UserTable";
import { setAdminUsers } from "../../../redux/reducers";
import AppLoader from "../../../components/loader";
import { useFetchAdminUser, useDeleteAdmin } from "../../../apolloClient/hooks/user/fetchHooks";
import { useAccess } from "../../../hooks/useAccess";
import { RESET_PASSWORD_QUERY, UNIQUE_ROLES } from "../../../apolloClient/operation/querys";
import { logEvent, USER_EVENTS, AUTH_EVENTS } from "../../../utils/amplitude";

interface DashboardProps {}

export const Users: React.FC<DashboardProps> = ({}) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [editUser, setEditUser] = useState<AdminUserModel>();

  const [addUser, setAdduser] = useState(false);
  const dispatch = useDispatch();

  const { adminUsers } = useAppSelector((state) => state.userReducer);

  const { loading, error, adminUsersData, refetch } = useFetchAdminUser({
    limit: 100,
    offset: 0,
  });

  const { deleteAdmin } = useDeleteAdmin();

  const { data: rolesData } = useQuery(UNIQUE_ROLES);

  useEffect(() => {
    if (!loading && !error && adminUsersData) {
      dispatch(setAdminUsers(adminUsersData));
      setIsLoading(false);
    } else if (error) {
      console.error("Error fetching users:", error);
      setIsLoading(false);
    }
  }, [loading, error, adminUsersData, dispatch, addUser]);

  // Use the global hook for access permissions
  const { loading: accessLoading } = useAccess();

  // Fetch permissions from Redux using useAppSelector
  const permissions = useAppSelector((state) => state.userReducer.permissions);

  const onAddUser = () => {
    setEditUser(undefined);
    setAdduser(!addUser);
  };

  const onTapEditUser = (editUserFromTable: AdminUserModel) => {
    setEditUser(editUserFromTable);
    setAdduser(true);
  };

  const [resetPasswordQuery, { data: resetPassData, error: resetPassError }] =
    useLazyQuery<{ resetPasswordAdmin: boolean }, { email: string }>(
      RESET_PASSWORD_QUERY,
      { fetchPolicy: "network-only" }
    );

  const resetPassword = async (email: string) => {
    try {
      const response = await resetPasswordQuery({
        variables: {
          email: email,
        },
      });

      if (response.data?.resetPasswordAdmin) {
        toast("Password reset email sent!", {
          type: "success",
        });
        
        // Track password reset request
        logEvent(AUTH_EVENTS.PASSWORD_RESET_REQUESTED, { email });
      } else {
        toast("User doesn't exist!", {
          type: "error",
        });
      }
    } catch (error) {
      toast("An error occurred while sending the reset password email.", {
        type: "error",
      });
    }
  };

  const handleDeleteUser = async (id: number) => {
    try {
      // Find user info before deleting for tracking
      const userToDelete = adminUsersData?.find(user => user.id === id);
      
      const response = await deleteAdmin(id);

      if (response.data?.deleteAdmin) {
        toast("User deleted successfully!", {
          type: "success",
        });
        
        // Track user deletion
        if (userToDelete) {
          logEvent(USER_EVENTS.USER_DELETED, { 
            user_id: id, 
            user_email: userToDelete.email 
          });
        }
        
        refetch();
      } else {
        toast("Failed to delete user!", {
          type: "error",
        });
      }
    } catch (error) {
      toast("An error occurred while deleting the user.", {
        type: "error",
      });
    }
  };

  if (isLoading || accessLoading) {
    return <AppLoader isLoading={isLoading || accessLoading} />;
  }

  // Check if the user has write access for the "Users" section
  const hasWriteAccess = permissions.some(
    (permission) => permission.name === "USERS" && permission.permission.includes("WRITE")
  );

  if (addUser) {
    return (
      <CreateUser
        editUser={editUser}
        onTapAdd={onAddUser}
        onFetchUsers={() => {
          onAddUser();
          refetch();
        }}
        roles={rolesData?.uniqueRoles?.roles} // Pass uniqueRoles to CreateUser
      />
    );
  } else {
    return (
      <UserTable
        onTapResetPassword={resetPassword}
        onTapEditUser={onTapEditUser}
        onTapDeleteUser={handleDeleteUser}
        users={adminUsersData}
        onTapAddUser={onAddUser}
        permissions={hasWriteAccess}
      />
    );
  }
};
